import React from 'react';
import { MessageSquareIcon, BrainIcon, ShieldCheckIcon, BarChart4Icon, DatabaseIcon } from 'lucide-react';
export const FeatureSection = () => {
  const features = [{
    icon: <MessageSquareIcon size={24} className="text-indigo-600" data-id="element-1865" />,
    title: 'Advanced AI Conversation Engine',
    description: 'Dynamic conversations that adapt based on candidate responses, with emotional intelligence and industry-specific models.',
    points: ['Natural follow-up questions based on responses', 'Sentiment analysis and emotional intelligence', 'Industry-specific conversation frameworks', 'Multi-language support with cultural awareness']
  }, {
    icon: <BrainIcon size={24} className="text-indigo-600" data-id="element-1866" />,
    title: 'Comprehensive Candidate Intelligence',
    description: 'Go beyond the resume with AI-powered research across social media, publications, and professional networks.',
    points: ['Social media and professional presence analysis', 'Publication and content evaluation', 'Professional network mapping', 'Skills validation through public work samples']
  }, {
    icon: <ShieldCheckIcon size={24} className="text-indigo-600" data-id="element-1867" />,
    title: 'AI Bot Detection & Quality Assurance',
    description: 'Protect your hiring process from AI-generated applications with sophisticated detection algorithms.',
    points: ['Multi-layer bot detection systems', 'Resume authenticity verification', 'Conversation authenticity assessment', 'Application quality scoring']
  }, {
    icon: <BarChart4Icon size={24} className="text-indigo-600" data-id="element-1868" />,
    title: 'Smart Candidate Comparison',
    description: 'Compare candidates across multiple dimensions with predictive performance modeling and bias detection.',
    points: ['Multi-dimensional scoring matrix', 'Predictive performance modeling', 'Bias detection and mitigation', 'Dynamic benchmarking against industry standards']
  }, {
    icon: <DatabaseIcon size={24} className="text-indigo-600" data-id="element-1869" />,
    title: 'Enterprise Integration',
    description: 'Seamlessly integrate with your existing HR technology stack including all major ATS platforms.',
    points: ['Universal ATS connectivity', 'HRIS integration capabilities', 'Calendar and scheduling intelligence', 'Workflow automation engine']
  }];
  return <section className="py-16 bg-gray-50" data-id="element-1870">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1871">
        <div className="text-center mb-16" data-id="element-1872">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-1873">
            Transforming Recruitment with AI
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-1874">
            Our platform combines advanced AI technologies to deliver
            comprehensive candidate insights and automated screening at scale.
          </p>
        </div>
        <div className="space-y-16" data-id="element-1875">
          {features.map((feature, index) => <div key={index} className={`flex flex-col ${index % 2 === 1 ? 'md:flex-row-reverse' : 'md:flex-row'} items-center`} data-id="element-1876">
              <div className="md:w-1/2 mb-8 md:mb-0" data-id="element-1877">
                <div className={`bg-white rounded-xl shadow-lg p-8 ${index % 2 === 1 ? 'md:mr-8' : 'md:ml-8'}`} data-id="element-1878">
                  <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center mb-6" data-id="element-1879">
                    {feature.icon}
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1880">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 mb-6" data-id="element-1881">{feature.description}</p>
                  <ul className="space-y-3" data-id="element-1882">
                    {feature.points.map((point, i) => <li key={i} className="flex items-start" data-id="element-1883">
                        <span className="h-6 w-6 flex-shrink-0 flex items-center justify-center rounded-full bg-indigo-100 text-indigo-600 mr-3" data-id="element-1884">
                          ✓
                        </span>
                        <span className="text-gray-600" data-id="element-1885">{point}</span>
                      </li>)}
                  </ul>
                </div>
              </div>
              <div className="md:w-1/2" data-id="element-1886">
                <div className={`bg-gradient-to-r from-indigo-500 to-purple-500 rounded-xl p-1 ${index % 2 === 0 ? 'md:mr-8' : 'md:ml-8'}`} data-id="element-1887">
                  <div className="bg-white rounded-lg p-6" data-id="element-1888">
                    {index === 0 && <div className="space-y-4" data-id="element-1889">
                        <div className="p-3 bg-gray-50 rounded-lg" data-id="element-1890">
                          <p className="text-sm font-medium" data-id="element-1891">
                            AI: Tell me about a challenging project you managed.
                          </p>
                        </div>
                        <div className="p-3 bg-indigo-50 rounded-lg ml-4" data-id="element-1892">
                          <p className="text-sm" data-id="element-1893">
                            Candidate: I led a team of 5 developers to rebuild
                            our payment processing system...
                          </p>
                        </div>
                        <div className="p-3 bg-gray-50 rounded-lg" data-id="element-1894">
                          <p className="text-sm font-medium" data-id="element-1895">
                            AI: That sounds interesting. How did you handle the
                            integration with existing systems?
                          </p>
                        </div>
                        <div className="p-3 bg-indigo-50 rounded-lg ml-4" data-id="element-1896">
                          <p className="text-sm" data-id="element-1897">
                            Candidate: We created a phased approach to minimize
                            disruption...
                          </p>
                        </div>
                        <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200" data-id="element-1898">
                          <p className="text-sm text-green-800 font-medium" data-id="element-1899">
                            Analysis: Candidate demonstrates strong project
                            management skills and technical understanding.
                            Follow-up questions about team leadership
                            recommended.
                          </p>
                        </div>
                      </div>}
                    {index === 1 && <div className="space-y-4" data-id="element-1900">
                        <div className="border-b pb-3" data-id="element-1901">
                          <h4 className="font-medium mb-2" data-id="element-1902">
                            Candidate Intelligence Report
                          </h4>
                          <div className="flex items-center text-sm text-gray-500" data-id="element-1903">
                            <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full mr-2" data-id="element-1904">
                              High Match
                            </span>
                            <span data-id="element-1905">Data compiled from 14 sources</span>
                          </div>
                        </div>
                        <div className="space-y-3" data-id="element-1906">
                          <div className="flex justify-between items-center" data-id="element-1907">
                            <span className="text-sm" data-id="element-1908">GitHub Activity</span>
                            <div className="w-32 bg-gray-200 rounded-full h-2" data-id="element-1909">
                              <div className="bg-indigo-600 h-2 rounded-full" style={{
                          width: '85%'
                        }} data-id="element-1910"></div>
                            </div>
                          </div>
                          <div className="flex justify-between items-center" data-id="element-1911">
                            <span className="text-sm" data-id="element-1912">
                              Technical Publications
                            </span>
                            <div className="w-32 bg-gray-200 rounded-full h-2" data-id="element-1913">
                              <div className="bg-indigo-600 h-2 rounded-full" style={{
                          width: '60%'
                        }} data-id="element-1914"></div>
                            </div>
                          </div>
                          <div className="flex justify-between items-center" data-id="element-1915">
                            <span className="text-sm" data-id="element-1916">Industry Influence</span>
                            <div className="w-32 bg-gray-200 rounded-full h-2" data-id="element-1917">
                              <div className="bg-indigo-600 h-2 rounded-full" style={{
                          width: '75%'
                        }} data-id="element-1918"></div>
                            </div>
                          </div>
                        </div>
                        <div className="text-sm" data-id="element-1919">
                          <p className="font-medium" data-id="element-1920">Key Insights:</p>
                          <ul className="list-disc pl-5 text-gray-600 space-y-1 mt-2" data-id="element-1921">
                            <li data-id="element-1922">
                              Active contributor to open-source ML projects
                            </li>
                            <li data-id="element-1923">Published 4 technical articles on medium</li>
                            <li data-id="element-1924">Regular speaker at industry conferences</li>
                          </ul>
                        </div>
                      </div>}
                    {index === 2 && <div className="space-y-4" data-id="element-1925">
                        <div className="border-b pb-3" data-id="element-1926">
                          <h4 className="font-medium mb-2" data-id="element-1927">AI Bot Detection</h4>
                          <div className="flex items-center text-sm text-gray-500" data-id="element-1928">
                            <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full mr-2" data-id="element-1929">
                              Authentic
                            </span>
                            <span data-id="element-1930">Application verified</span>
                          </div>
                        </div>
                        <div className="space-y-3" data-id="element-1931">
                          <div className="flex items-center" data-id="element-1932">
                            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center mr-3" data-id="element-1933">
                              <span className="text-green-600" data-id="element-1934">✓</span>
                            </div>
                            <div data-id="element-1935">
                              <p className="text-sm font-medium" data-id="element-1936">
                                Resume Authenticity
                              </p>
                              <p className="text-xs text-gray-500" data-id="element-1937">
                                No AI-generated content detected
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center" data-id="element-1938">
                            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center mr-3" data-id="element-1939">
                              <span className="text-green-600" data-id="element-1940">✓</span>
                            </div>
                            <div data-id="element-1941">
                              <p className="text-sm font-medium" data-id="element-1942">
                                Conversation Patterns
                              </p>
                              <p className="text-xs text-gray-500" data-id="element-1943">
                                Natural speech patterns confirmed
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center" data-id="element-1944">
                            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center mr-3" data-id="element-1945">
                              <span className="text-green-600" data-id="element-1946">✓</span>
                            </div>
                            <div data-id="element-1947">
                              <p className="text-sm font-medium" data-id="element-1948">
                                Application Consistency
                              </p>
                              <p className="text-xs text-gray-500" data-id="element-1949">
                                Consistent details across channels
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>}
                    {index === 3 && <div className="space-y-4" data-id="element-1950">
                        <div className="border-b pb-3" data-id="element-1951">
                          <h4 className="font-medium mb-2" data-id="element-1952">
                            Candidate Comparison
                          </h4>
                          <div className="flex items-center text-sm text-gray-500" data-id="element-1953">
                            <span data-id="element-1954">Comparing top 3 candidates</span>
                          </div>
                        </div>
                        <div className="space-y-4" data-id="element-1955">
                          <div data-id="element-1956">
                            <div className="flex justify-between mb-1" data-id="element-1957">
                              <span className="text-sm font-medium" data-id="element-1958">
                                Technical Skills
                              </span>
                              <span className="text-sm text-gray-500" data-id="element-1959">
                                85/100
                              </span>
                            </div>
                            <div className="flex space-x-1" data-id="element-1960">
                              <div className="h-2 bg-indigo-600 rounded-l-full w-1/3" data-id="element-1961"></div>
                              <div className="h-2 bg-blue-400 w-1/4" data-id="element-1962"></div>
                              <div className="h-2 bg-gray-300 rounded-r-full w-5/12" data-id="element-1963"></div>
                            </div>
                          </div>
                          <div data-id="element-1964">
                            <div className="flex justify-between mb-1" data-id="element-1965">
                              <span className="text-sm font-medium" data-id="element-1966">
                                Leadership
                              </span>
                              <span className="text-sm text-gray-500" data-id="element-1967">
                                92/100
                              </span>
                            </div>
                            <div className="flex space-x-1" data-id="element-1968">
                              <div className="h-2 bg-indigo-600 rounded-l-full w-2/5" data-id="element-1969"></div>
                              <div className="h-2 bg-blue-400 w-1/5" data-id="element-1970"></div>
                              <div className="h-2 bg-gray-300 rounded-r-full w-2/5" data-id="element-1971"></div>
                            </div>
                          </div>
                          <div data-id="element-1972">
                            <div className="flex justify-between mb-1" data-id="element-1973">
                              <span className="text-sm font-medium" data-id="element-1974">
                                Cultural Fit
                              </span>
                              <span className="text-sm text-gray-500" data-id="element-1975">
                                78/100
                              </span>
                            </div>
                            <div className="flex space-x-1" data-id="element-1976">
                              <div className="h-2 bg-indigo-600 rounded-l-full w-1/4" data-id="element-1977"></div>
                              <div className="h-2 bg-blue-400 w-1/3" data-id="element-1978"></div>
                              <div className="h-2 bg-gray-300 rounded-r-full w-5/12" data-id="element-1979"></div>
                            </div>
                          </div>
                        </div>
                        <div className="text-sm" data-id="element-1980">
                          <p className="font-medium text-indigo-600" data-id="element-1981">
                            Recommendation:
                          </p>
                          <p className="text-gray-600 mt-1" data-id="element-1982">
                            Sarah J. demonstrates strongest leadership skills
                            and technical capabilities. Predicted success rate:
                            94%
                          </p>
                        </div>
                      </div>}
                    {index === 4 && <div className="space-y-4" data-id="element-1983">
                        <div className="border-b pb-3" data-id="element-1984">
                          <h4 className="font-medium mb-2" data-id="element-1985">
                            Enterprise Integration
                          </h4>
                          <div className="flex items-center text-sm text-gray-500" data-id="element-1986">
                            <span data-id="element-1987">Connected with your HR ecosystem</span>
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-3" data-id="element-1988">
                          {['Workday', 'Greenhouse', 'SuccessFactors', 'Lever', 'BambooHR', 'Jobvite'].map((ats, i) => <div key={i} className="p-2 bg-gray-100 rounded text-center text-sm" data-id="element-1989">
                              {ats}
                            </div>)}
                        </div>
                        <div className="p-3 bg-indigo-50 rounded-lg" data-id="element-1990">
                          <div className="flex items-center" data-id="element-1991">
                            <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center mr-3" data-id="element-1992">
                              <span className="text-indigo-600" data-id="element-1993">→</span>
                            </div>
                            <div data-id="element-1994">
                              <p className="text-sm font-medium" data-id="element-1995">
                                Automated Workflow
                              </p>
                              <p className="text-xs text-gray-500" data-id="element-1996">
                                Candidate data synced to Greenhouse
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="p-3 bg-green-50 rounded-lg" data-id="element-1997">
                          <div className="flex items-center" data-id="element-1998">
                            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center mr-3" data-id="element-1999">
                              <span className="text-green-600" data-id="element-2000">✓</span>
                            </div>
                            <div data-id="element-2001">
                              <p className="text-sm font-medium" data-id="element-2002">
                                Interview Scheduled
                              </p>
                              <p className="text-xs text-gray-500" data-id="element-2003">
                                Calendar invite sent to hiring manager
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>}
                  </div>
                </div>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
};